import React from 'react';

/**
 * Device frame drawn in CSS so the screenshots inside stay swappable.
 * Screens live in /public/screens at 738x1600 (2x) — raw captures, no bezel,
 * which lets one frame cross-fade between any of them without jitter.
 *
 * The frame itself is staged: titanium edge gradient, side buttons, dynamic
 * island, glass glare and a floor shadow — so a raw screenshot reads as a
 * physical object, not a rectangle.
 */
const PhoneFrame = ({
  src,
  alt,
  width = 300,
  className = '',
  glow = true,
  floorShadow = true,
  glowColor = 'rgba(147,51,234,0.30)',
  priority = false,
  children,
}) => {
  // 738x1600 screen ratio, frame adds ~5.2% total
  const height = Math.round(width * (1600 / 738));
  const btn = {
    position: 'absolute',
    width: Math.max(2.5, width * 0.011),
    borderRadius: 2,
    background: 'linear-gradient(90deg, #3f3f4d, #16161d)',
  };

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {glow && (
        <div
          aria-hidden="true"
          className="absolute -inset-x-14 -inset-y-10 pointer-events-none"
          style={{
            background: `radial-gradient(closest-side, ${glowColor}, transparent 74%)`,
            }}
        />
      )}

      {/* Side buttons */}
      <span aria-hidden="true" style={{ ...btn, left: -2, top: '17%', height: '3.6%' }} />
      <span aria-hidden="true" style={{ ...btn, left: -2, top: '24%', height: '6.4%' }} />
      <span aria-hidden="true" style={{ ...btn, left: -2, top: '32%', height: '6.4%' }} />
      <span aria-hidden="true" style={{ ...btn, right: -2, top: '25%', height: '9.5%' }} />

      {/* Bezel */}
      <div
        className="relative w-full h-full overflow-hidden"
        style={{
          borderRadius: `${width * 0.155}px`,
          padding: width * 0.02,
          background:
            'linear-gradient(150deg, #454553 0%, #141419 30%, #0b0b10 55%, #32323e 88%, #4a4a58 100%)',
          boxShadow:
            '0 50px 100px -30px rgba(0,0,0,0.85), 0 18px 40px -18px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.07)',
        }}
      >
        {/* Screen */}
        <div
          className="relative w-full h-full overflow-hidden bg-ston-bg"
          style={{
            borderRadius: `${width * 0.135}px`,
            boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.9)',
          }}
        >
          {children || (
            <img
              src={src}
              // 480px variants live in /screens/sm — phones pick them for small renders
              srcSet={
                src.startsWith('/screens/')
                  ? `${src.replace('/screens/', '/screens/sm/')} 480w, ${src} 738w`
                  : undefined
              }
              sizes={src.startsWith('/screens/') ? `${width}px` : undefined}
              alt={alt}
              width={738}
              height={1600}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={priority ? 'high' : 'auto'}
              className="w-full h-full object-cover object-top"
            />
          )}

          {/* Dynamic island */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 rounded-full bg-black"
            style={{
              top: '1.7%',
              width: '25%',
              height: '3.1%',
              boxShadow: 'inset 0 0 2px rgba(255,255,255,0.08)',
            }}
          >
            <span
              className="absolute rounded-full"
              style={{
                right: '12%',
                top: '28%',
                width: '18%',
                height: '44%',
                background:
                  'radial-gradient(circle at 40% 40%, #1e2733 0%, #05070a 70%)',
              }}
            />
          </div>

          {/* Glass glare */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(112deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.02) 22%, transparent 34%), linear-gradient(248deg, rgba(255,255,255,0.03) 0%, transparent 18%)',
            }}
          />
        </div>
      </div>

      {floorShadow && (
        <div
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            bottom: -Math.round(width * 0.12),
            left: '8%',
            right: '8%',
            height: Math.round(width * 0.16),
            background:
              'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(0,0,0,0.65), transparent 70%)',
            filter: 'blur(12px)',
          }}
        />
      )}
    </div>
  );
};

export default PhoneFrame;
